import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import {
  HEARTBEAT_MS,
  newPlatformId,
  platformPageOf,
  PlatformActivityTracker,
  type PlatformSessionStore,
  type StoredPlatformSession,
} from "./platformActivity";
import { PlatformActivityService } from "./platformActivity.service";

const SESSION_KEY = "coloca:platform-session";
const INPUT_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

// localStorage can throw (private windows, blocked site data); a memory copy keeps the session for the tab.
function sessionStore(): PlatformSessionStore {
  let memory: StoredPlatformSession | null = null;
  return {
    read() {
      try {
        const raw = window.localStorage.getItem(SESSION_KEY);
        return raw ? (JSON.parse(raw) as StoredPlatformSession) : memory;
      } catch {
        return memory;
      }
    },
    write(session) {
      memory = session;
      try {
        window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      } catch {
        // The memory copy is enough for this tab.
      }
    },
  };
}

/**
 * Reports which dashboard page is in use and for how long, for the staff platform metrics: the page opening,
 * a heartbeat each minute while the tab is visible and touched, the tab hiding and the page being left. Fire
 * and forget: nothing on screen depends on it, and a failed report is simply sent again with the next one.
 */
export function usePlatformActivity(userId: string | null | undefined): void {
  const { pathname } = useLocation();
  const { page, section } = useMemo(() => platformPageOf(pathname), [pathname]);
  const trackerRef = useRef<PlatformActivityTracker | null>(null);

  useEffect(() => {
    if (!userId) return;
    const tracker = new PlatformActivityTracker({
      userId,
      send: (visit) => void PlatformActivityService.recordVisits([visit]),
      sessions: sessionStore(),
      newId: newPlatformId,
    });
    trackerRef.current = tracker;

    const onVisibility = () => {
      if (document.hidden) tracker.hide(Date.now());
      else tracker.resume(Date.now());
    };
    let lastInput = 0;
    const onInput = () => {
      const now = Date.now();
      // One mark per few seconds is plenty to know someone is there.
      if (now - lastInput < 5_000) return;
      lastInput = now;
      tracker.input(now);
    };
    const onPageHide = () => tracker.close(Date.now());
    const timer = window.setInterval(() => {
      if (!document.hidden) tracker.beat(Date.now());
    }, HEARTBEAT_MS);

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    for (const event of INPUT_EVENTS) window.addEventListener(event, onInput, { passive: true });
    return () => {
      tracker.close(Date.now());
      trackerRef.current = null;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      for (const event of INPUT_EVENTS) window.removeEventListener(event, onInput);
    };
  }, [userId]);

  // A new visit per page, not per path: a card's detail is still the cards page.
  useEffect(() => {
    trackerRef.current?.open({ page, section }, Date.now(), !document.hidden);
  }, [page, section, userId]);
}
